import { Helmet } from 'react-helmet-async';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'po文学指南',
    url: 'https://powenxue.com',
    description: '专注于po文学创作技巧与行业趋势的综合性知识平台，致力于帮助每一位创作者写出打动人心的作品。',
    foundingDate: '2024-03',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@powenxue.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于po文学指南',
    description: 'po文学指南是专注于po文学创作技巧与行业趋势的综合性知识平台，致力于帮助每一位创作者写出打动人心的作品。',
    mainEntity: {
      '@type': 'Organization',
      name: 'po文学指南',
      description: '专注于po文学创作技巧与行业趋势的综合性知识平台',
    },
  };

  return (
    <>
      <Helmet>
        <title>关于我们 - po文学指南</title>
        <meta name="description" content="po文学指南是专注于po文学创作技巧与行业趋势的综合性知识平台。了解我们的品牌故事、行业口碑与联系方式，加入po文学创作者社区。" />
        <meta name="keywords" content="po文学指南,关于我们,po文学平台,联系我们" />
        <link rel="canonical" href="https://powenxue.com/about" />
        <meta property="og:title" content="关于我们 - po文学指南" />
        <meta property="og:description" content="了解po文学指南的品牌故事、行业口碑与联系方式。" />
        <meta property="og:url" content="https://powenxue.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="关于我们 - po文学指南" />
        <meta name="twitter:description" content="了解po文学指南的品牌故事、行业口碑与联系方式。" />
        <script type="application/ld+json">
          {JSON.stringify(orgSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(aboutSchema)}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="mt-8 mb-16">
          <div className="text-center mb-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-500 flex items-center justify-center">
              <i className="ri-quill-pen-line text-background-50 text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于po文学指南
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              我们是一个专注于po文学创作技巧与行业趋势的综合性知识平台，致力于帮助每一位创作者写出打动人心的作品。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                我们的品牌故事
              </h2>
              <div className="space-y-4 text-foreground-700 text-sm leading-relaxed">
                <p>
                  <strong>po文学指南</strong>创立于2024年初，由一个热爱文字创作的小团队发起。创始团队由资深网络文学编辑、一线po文创作者和新媒体运营专家组成，拥有丰富的行业经验和对po文学发展趋势的深刻洞察。
                </p>
                <p>
                  我们观察到，随着碎片化阅读习惯的普及，越来越多的年轻人开始尝试po文创作，但缺乏系统的学习资源和专业的指导。于是，po文学指南应运而生——我们希望搭建一个专业、系统、友好的学习平台，让每一个热爱文字的人都能在这里找到成长的方向。
                </p>
                <p>
                  从最初的一篇入门教程开始，到今天覆盖<strong>入门指南、创作技巧、风格解析、经典案例、行业趋势</strong>五大内容板块，po文学指南已经成长为po文学领域最具影响力的知识平台之一。我们的内容被数十万创作者阅读和引用，成为许多人po文创作之路上的第一站。
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20creative%20studio%20workspace%20with%20soft%20ambient%20light%2C%20cream%20and%20beige%20tones%2C%20elegant%20wooden%20desk%20with%20laptop%20notebook%20and%20coffee%2C%20bookshelf%20background%2C%20clean%20composition%2C%20cozy%20creative%20atmosphere%2C%20golden%20hour%20warm%20lighting%2C%20professional%20yet%20inviting%20mood%2C%20no%20text&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="po文学指南创作工作室"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="mb-16 bg-background-100 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              行业口碑
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              来自创作者的真实评价
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="font-heading font-bold text-accent-700 text-sm">林</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">林小月</p>
                  <p className="text-xs text-foreground-500">po文创作者 · 3年经验</p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                "po文学指南是我入行时最重要的学习资源。从零基础到写出第一篇10万+阅读的po文，这里的每一篇教程都给了我实实在在的帮助。特别推荐创作技巧系列，真的非常实用。"
              </p>
            </div>

            <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                  <span className="font-heading font-bold text-secondary-700 text-sm">陈</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">陈远航</p>
                  <p className="text-xs text-foreground-500">网络文学编辑 · 5年经验</p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                "作为从业五年的编辑，我见证了许多po文创作者的成长。po文学指南的内容专业度在行业内是公认的，无论是风格解析还是案例拆解，都展现了非常深入的行业洞察。"
              </p>
            </div>

            <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="font-heading font-bold text-primary-700 text-sm">王</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">王雨晴</p>
                  <p className="text-xs text-foreground-500">大学生创作者 · 1年经验</p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                "大一的时候在室友推荐下关注了po文学指南，从此打开了新世界的大门。这里的教程特别适合新手，讲得很清楚。现在我已经是学校文学社的创作骨干了，感谢po文学指南的陪伴。"
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="mb-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
              联系我们
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              期待与你的交流
            </h2>
            <p className="text-foreground-500 text-sm mt-3 max-w-lg mx-auto">
              无论你有创作问题、合作意向还是任何建议，都欢迎通过以下方式联系我们
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/60">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                <i className="ri-mail-line text-primary-600 text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">电子邮箱</h3>
              <p className="text-foreground-500 text-sm">contact@powenxue.com</p>
            </div>

            <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/60">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-50 flex items-center justify-center">
                <i className="ri-wechat-line text-accent-600 text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">微信公众号</h3>
              <p className="text-foreground-500 text-sm">搜索关注"po文学指南"</p>
            </div>

            <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/60">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-secondary-50 flex items-center justify-center">
                <i className="ri-weibo-line text-secondary-600 text-xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">官方微博</h3>
              <p className="text-foreground-500 text-sm">@po文学指南</p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}